import { HttpErrorResponse } from '@angular/common/http';

import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { OfferItem, OfferStatus, Position } from '@recruitee/http/types';
import { createEntityCollectionStore, memoizeSelector, pipeState } from '@recruitee/ngrx';

import { CanAssignToJobsWithStatuses, EntityStoreKey, OfferScope } from '../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferItem>()(
  EntityStoreKey.JobItems,
  {
    customActions: adapter => ({
      fetchCollection: (state, payload: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
      fetchActiveCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Active, entities: [] }),
      fetchArchivedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Archived, entities: [] }),
      upsertOne: (state, payload: { data: OfferItem; scope: OfferScope }) =>
        adapter.upsertOne(state, payload.data, { scope: payload.scope }),
      updatePositions: (state, payload: Position[]) =>
        pipeState(state, next =>
          adapter.markAsPending(next, { scope: OfferScope.Active, entities: [] }),
        ),
      updatePositionSuccess: (state, payload: Position[]) => {
        const newOrder = [...payload].sort((itemA, itemB) => itemB.position - itemA.position);
        const orderedOffers = <OfferItem[]>newOrder.map(item => ({ ...state.entityMap[item.id] }!));
        return pipeState(
          state,
          next => adapter.setMany(next, orderedOffers, { scope: OfferScope.Active }),
          next => adapter.markAsLoaded(next, { scope: OfferScope.Active, entities: [] }),
        );
      },
      updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
        pipeState(state, next =>
          adapter.setError(next, payload.error, { scope: OfferScope.Active, entities: [] }),
        ),
      references: (state, payload: { data: OfferItem[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const active = createSelector(selectors.entities({ scope: OfferScope.Active }), state => state);
const archived = createSelector(selectors.entities({ scope: OfferScope.Archived }), state => state);
const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);
const activeFollowed = createSelector(active, state => state.filter(job => job.followed));
const activeByStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(active, (state: OfferItem[]) =>
    state.filter(job => statuses.includes(job.status)),
  ),
);
const activeByNotStatus = memoizeSelector((statuses: OfferStatus[]) =>
  createSelector(active, (state: OfferItem[]) =>
    state.filter(job => !statuses.includes(job.status)),
  ),
);
const activeBySlug = memoizeSelector((slug: string) =>
  createSelector(active, (state: OfferItem[]) => state.find(job => job.slug === slug)),
);
const bySlug = memoizeSelector((slug: string) =>
  createSelector(all, (state: OfferItem[]) => state.find(job => job.slug === slug)),
);
const canAssignTo = activeByStatus(CanAssignToJobsWithStatuses);

// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);
const archivedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Archived }),
  state => state,
);

const byIds = memoizeSelector((offerIds: number[]) =>
  createSelectorFactory(defaultMemoize)(
    ...(offerIds || []).map(offerId => selectors.byId(offerId)),
    (...result: OfferItem[]) => result,
  ),
);

const customSelectors = {
  all,
  active,
  archived,
  activeFollowed,
  activeByStatus,
  activeBySlug,
  canAssignTo,
  bySlug,
  activeByNotStatus,
  activeScopeIsLoaded,
  archivedScopeIsLoaded,
  byIds,
};
const jobsSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  actions as JobItemsActions,
  namespace as JobItemsNamespace,
  jobsSelectors as JobItemsSelectors,
  reducer as JobItemsReducer,
};
